import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Input from '../../components/Common/Input';
import Select from '../../components/Common/Select';
import Textarea from '../../components/Common/Textarea';
import api from '../../utils/api';
import { showSuccess, showError } from '../../utils/swal';
import {
  ArrowLeft,
  CheckCircle,
  Plus,
  Trash2,
  Home,
  Search,
  X
} from 'lucide-react';

const MeetingComplete = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    customer_id: '',
    date: '',
    type: 'face_to_face',
    outcome: '',
    follow_up_type: '',
    follow_up_date: '',
    notes: '',
  });

  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      const response = await api.get('/customers');
      setCustomers(response.data.data || response.data);
    } catch (e) {}
  };

  // Dinamik opsiyonlar
  const [options, setOptions] = useState({
    property_types: [],
    residential_types: [],
    listing_types: [],
    sale_types: [],
    room_definitions: [],
    facade_types: [],
    cities: [],
    payment_preferences: [],
    savings_reasons: [],
  });

  const [portfolios, setPortfolios] = useState([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState('');

  // İlgilendiği portföyler (yeni bölüm)
  const [interestedPortfolioIds, setInterestedPortfolioIds] = useState([]);
  const [interestedPortfolioSelectId, setInterestedPortfolioSelectId] = useState('');

  // Her tercih için ayrı ilçe/mahalle listesi tutmamız lazım
  const [prefDistricts, setPrefDistricts] = useState({});
  const [prefNeighborhoods, setPrefNeighborhoods] = useState({});

  const emptyPreference = {
    property_type_id: '',
    payment_preference_id: '',
    savings_reason_id: '',
    listing_type_id: '',
    residential_type_id: '',
    room_definition_id: '',
    facade_type_id: '',
    city_id: '',
    district_id: '',
    neighborhood_id: '',
    min_floor: '',
    max_floor: '',
    has_elevator: false,
    min_sqm: '',
    max_sqm: '',
    min_price: '',
    max_price: '',
    min_building_age: '',
    max_building_age: '',
    additional_notes: '',
  };

  const [propertyPreference, setPropertyPreference] = useState({ ...emptyPreference });

  useEffect(() => {
    fetchOptions();
    fetchMeeting();
    fetchPortfolios();
  }, [id]);

  const fetchOptions = async () => {
    try {
      const response = await api.get('/portfolio-options');
      setOptions(response.data);
    } catch (error) {
      console.error('Opsiyonlar yüklenemedi:', error);
    }
  };

  const fetchPortfolios = async () => {
    try {
      const response = await api.get('/portfolios');
      const portfolioData = response.data.data || response.data;
      setPortfolios(portfolioData);
    } catch (error) {
      console.error('Portföyler yüklenemedi:', error);
    }
  };

  const fetchMeeting = async () => {
    try {
      const response = await api.get(`/meetings/${id}`);
      const meetingData = response.data;

      // Format date for input
      let formattedDate = '';
      if (meetingData.date) {
        const date = new Date(meetingData.date);
        formattedDate = date.toISOString().slice(0, 16);
      }

      setMeeting(meetingData);
      setFormData({
        customer_id: meetingData.customer_id || '',
        date: formattedDate,
        type: meetingData.type || 'face_to_face',
        outcome: meetingData.outcome || '',
        follow_up_type: meetingData.type || 'face_to_face',
        follow_up_date: meetingData.follow_up_date || '',
        notes: meetingData.notes || '',
      });

      if (meetingData.is_completed) {
        if (meetingData.customer_property_preferences) {
          setPropertyPreference({ ...emptyPreference, ...meetingData.customer_property_preferences });
          if (meetingData.customer_property_preferences.city_id) fetchDistricts(meetingData.customer_property_preferences.city_id);
          if (meetingData.customer_property_preferences.district_id) fetchNeighborhoods(meetingData.customer_property_preferences.district_id);
        }
        if (meetingData.interested_portfolio_ids && Array.isArray(meetingData.interested_portfolio_ids)) {
          setInterestedPortfolioIds(meetingData.interested_portfolio_ids);
        }
      }

      setLoading(false);
    } catch (error) {
      console.error('Görüşme yüklenemedi:', error);
      showError('Görüşme yüklenirken bir hata oluştu');
      setLoading(false);
    }
  };

  const [districts, setDistricts] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);

  const fetchDistricts = async (cityId) => {
    try {
      const response = await api.get('/districts', { params: { city_id: cityId } });
      setDistricts(response.data);
    } catch (error) {
      console.error('İlçeler yüklenemedi:', error);
    }
  };

  const fetchNeighborhoods = async (districtId) => {
    try {
      const response = await api.get('/neighborhoods', { params: { district_id: districtId } });
      setNeighborhoods(response.data);
    } catch (error) {
      console.error('Mahalleler yüklenemedi:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // ---------- İlgilendiği Portföyler ----------
  const handleAddInterestedPortfolio = () => {
    if (!interestedPortfolioSelectId) return;
    const portfolioId = parseInt(interestedPortfolioSelectId);
    if (interestedPortfolioIds.includes(portfolioId)) return;
    setInterestedPortfolioIds(prev => [...prev, portfolioId]);
    setInterestedPortfolioSelectId('');
  };

  const handleRemoveInterestedPortfolio = (portfolioId) => {
    setInterestedPortfolioIds(prev => prev.filter(id => id !== portfolioId));
  };

  // ---------- Portföy Seçerek Tercih Oluştur ----------
  const handlePortfolioSelect = (e) => {
    const portfolioId = e.target.value;
    setSelectedPortfolioId(portfolioId);

    if (!portfolioId) return;

    const portfolio = portfolios.find(p => p.id === parseInt(portfolioId));
    if (!portfolio) return;

    // Portföy bilgilerinden FK bazlı tercih oluştur
    const newPreference = {
      property_type_id: portfolio.property_type_id || '',
      listing_type_id: portfolio.listing_type_id || '',
      residential_type_id: portfolio.residential_type_id || '',
      room_definition_id: portfolio.room_definition_id || '',
      facade_type_id: portfolio.facade_type_id || '',
      city_id: portfolio.city_id || '',
      district_id: portfolio.district_id || '',
      neighborhood_id: portfolio.neighborhood_id || '',
      min_floor: portfolio.floor || '',
      max_floor: portfolio.floor || '',
      has_elevator: false,
      min_sqm: portfolio.net_sqm || portfolio.gross_sqm || '',
      max_sqm: portfolio.net_sqm || portfolio.gross_sqm || '',
      min_price: portfolio.list_price || '',
      max_price: portfolio.list_price || '',
      min_building_age: portfolio.building_age || '',
      max_building_age: portfolio.building_age || '',
      additional_notes: `${portfolio.title} portföyüne göre oluşturuldu`,
    };

    setPropertyPreference(newPreference);

    // Seçilen portföyün konum bilgilerini yükle
    if (portfolio.city_id) {
      fetchDistricts(portfolio.city_id);
    }
    if (portfolio.district_id) {
      fetchNeighborhoods(portfolio.district_id);
    }
  };

  const handlePreferenceChange = (field, value) => {
    if (field === 'city_id') {
      setPropertyPreference(prev => ({
        ...prev,
        city_id: value,
        district_id: '',
        neighborhood_id: '',
      }));
      if (value) {
        fetchDistricts(value);
      } else {
        setDistricts([]);
      }
      setNeighborhoods([]);
      return;
    }

    if (field === 'district_id') {
      setPropertyPreference(prev => ({
        ...prev,
        district_id: value,
        neighborhood_id: '',
      }));
      if (value) {
        fetchNeighborhoods(value);
      } else {
        setNeighborhoods([]);
      }
      return;
    }

    setPropertyPreference(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.outcome) {
      showError('Lütfen görüşme sonucunu seçiniz');
      return;
    }

    setSubmitting(true);
    try {
      const isPreferenceValid =
        propertyPreference.property_type_id ||
        propertyPreference.city_id ||
        propertyPreference.min_price ||
        propertyPreference.max_price ||
        propertyPreference.room_definition_id;

      const payload = {
        ...formData,
        property_preference: isPreferenceValid ? propertyPreference : null,
        interested_portfolio_ids: interestedPortfolioIds.length > 0 ? interestedPortfolioIds : null,
      };

      if (meeting.is_completed) {
        // It's already completed, use update method
        await api.put(`/meetings/${id}`, payload);
        showSuccess('Görüşme başarıyla güncellendi');
      } else {
        // Complete the meeting
        await api.post(`/meetings/${id}/complete`, payload);
        showSuccess('Görüşme başarıyla tamamlandı');
      }

      if (formData.follow_up_date) {
        try {
          await api.post('/meetings', {
            customer_id: meeting.customer_id,
            date: formData.follow_up_date,
            type: formData.follow_up_type || 'face_to_face',
            is_completed: false,
            notes: `${meeting.customer?.name} ${meeting.customer?.surname} ile yapılan görüşmenin takibi`,
          });
        } catch (error) {
          console.error('Yeni görüşme oluşturulamadı:', error);
        }
      }

      if (!meeting.is_completed) {
        showSuccess('Görüşme başarıyla tamamlandı');
      }
      navigate(`/meetings/${id}`);
    } catch (error) {
      console.error('Görüşme tamamlanamadı:', error);
      showError(error.response?.data?.message || 'Görüşme tamamlanırken bir hata oluştu');
    } finally {
      setSubmitting(false);
    }
  };

  // İlgilendiği portföylerin detaylarını al
  const getInterestedPortfolioDetails = () => {
    return interestedPortfolioIds
      .map(pid => portfolios.find(p => p.id === pid))
      .filter(Boolean);
  };

  // İlgilendiği portföylere eklenmemiş portföyleri filtrele (select options)
  const availablePortfoliosForInterest = portfolios.filter(
    p => !interestedPortfolioIds.includes(p.id)
  );

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  if (!meeting) {
    return <div className="text-center py-8">Görüşme bulunamadı</div>;
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <Button variant="secondary" size="sm" onClick={() => navigate(`/meetings/${id}`)}>
          <ArrowLeft size={16} />
          Geri
        </Button>
      </div>

      {/* Header */}
      <Card className="mb-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle size={24} className="text-green-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {meeting.is_completed ? 'Görüşmeyi Güncelle' : 'Görüşmeyi Tamamla'}
            </h1>
            <p className="text-gray-600">
              {meeting.customer?.name} {meeting.customer?.surname} - {new Date(meeting.date).toLocaleDateString('tr-TR')}
            </p>
          </div>
        </div>
      </Card>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* CARD 0: Temel Görüşme Bilgileri (Eğer Düzenleniyorsa) */}
        {meeting.is_completed && (
          <Card title="Görüşme Bilgileri">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Müşteri"
                name="customer_id"
                value={formData.customer_id}
                onChange={handleChange}
                options={customers.map(c => ({
                  value: c.id,
                  label: `${c.name} ${c.surname}`
                }))}
                required
              />
              <Input
                label="Görüşme Tarihi"
                name="date"
                type="datetime-local"
                value={formData.date}
                onChange={handleChange}
                required
              />
              <Select
                label="Görüşme Tipi"
                name="type"
                value={formData.type}
                onChange={handleChange}
                options={[
                  { value: 'face_to_face', label: 'Yüz Yüze' },
                  { value: 'call', label: 'Arama' },
                  { value: 'email', label: 'E-posta' },
                  { value: 'whatsapp', label: 'WhatsApp' },
                  { value: 'other', label: 'Diğer' },
                ]}
                required
              />
            </div>
          </Card>
        )}

        {/* ═══════════════════════════════════════════════ */}
        {/* CARD 1: Görüşme Sonuçları */}
        {/* ═══════════════════════════════════════════════ */}
        <Card title="📋 Görüşme Sonuçları">
          <div className="space-y-4">
            <Select
              label="Görüşme Sonucu *"
              name="outcome"
              value={formData.outcome}
              onChange={handleChange}
              required
              options={[
                { value: '', label: 'Seçiniz' },
                { value: 'Satış', label: 'Satış' },
                { value: 'Takip Edilecek', label: 'Takip Edilecek' },
                { value: 'Olumsuz', label: 'Olumsuz' },
                { value: 'İlgileniyor', label: 'İlgileniyor' },
                { value: 'Düşünüyor', label: 'Düşünüyor' },
              ]}
            />

            <Select
              label="Bir Sonraki Görüşme Tipi"
              name="follow_up_type"
              value={formData.follow_up_type}
              onChange={handleChange}
              options={[
                { value: 'face_to_face', label: 'Yüz Yüze' },
                { value: 'call', label: 'Arama' },
                { value: 'email', label: 'E-posta' },
                { value: 'whatsapp', label: 'WhatsApp' },
              ]}
            />

            <Input
              label="Bir Sonraki Görüşme Tarihi"
              name="follow_up_date"
              type="date"
              value={formData.follow_up_date}
              onChange={handleChange}
            />

            <Textarea
              label="Görüşme Notları"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={4}
              placeholder="Görüşme detayları, müşteri talepleri, vs..."
            />
          </div>
        </Card>

        {/* ═══════════════════════════════════════════════ */}
        {/* CARD 2: Müşterinin İlgilendiği Portföyler */}
        {/* ═══════════════════════════════════════════════ */}
        <Card title="🏠 Müşterinin İlgilendiği Portföyler">
          <div className="space-y-4">
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Select
                  label="Portföy Seçiniz"
                  value={interestedPortfolioSelectId}
                  onChange={(e) => setInterestedPortfolioSelectId(e.target.value)}
                  options={[
                    { value: '', label: 'Portföy seçiniz...' },
                    ...availablePortfoliosForInterest.map(p => ({
                      value: p.id,
                      label: `${p.title} - ${p.city?.name || ''} ${p.district?.name || ''}`
                    }))
                  ]}
                />
              </div>
              <Button
                type="button"
                size="sm"
                onClick={handleAddInterestedPortfolio}
                disabled={!interestedPortfolioSelectId}
              >
                <Plus size={16} />
                Ekle
              </Button>
            </div>

            {/* Seçilen portföylerin listesi */}
            {getInterestedPortfolioDetails().length > 0 ? (
              <div className="space-y-2">
                {getInterestedPortfolioDetails().map((portfolio) => (
                  <div
                    key={portfolio.id}
                    className="flex items-center justify-between p-3 bg-emerald-100 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-emerald-100 rounded-full flex items-center justify-center">
                        <Home size={14} className="text-gray-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 text-sm">{portfolio.title}</p>
                        <p className="text-xs text-gray-500">
                          {portfolio.city?.name} {portfolio.district?.name && `/ ${portfolio.district.name}`}
                          {portfolio.list_price && ` — ${parseFloat(portfolio.list_price).toLocaleString('tr-TR')} ₺`}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveInterestedPortfolio(portfolio.id)}
                      className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-all"
                      title="Kaldır"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-400 text-sm">
                <Search size={24} className="mx-auto mb-2 opacity-50" />
                Henüz portföy seçilmedi
              </div>
            )}

            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
              <p className="text-sm text-emerald-800">
                💡 <strong>İpucu:</strong> Müşterinin bu görüşmede ilgi gösterdiği portföyleri buradan ekleyebilirsiniz.
              </p>
            </div>
          </div>
        </Card>

        {/* ═══════════════════════════════════════════════ */}
        {/* CARD 3: Müşteri Gayrimenkul Tercihleri */}
        {/* ═══════════════════════════════════════════════ */}
        <Card title="🔍 Müşteri Gayrimenkul Tercihleri">
          {/* Portföy Seçerek Otomatik Doldur */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <Select
              label="Portföy Seçerek Otomatik Doldur (Opsiyonel)"
              value={selectedPortfolioId}
              onChange={handlePortfolioSelect}
              options={[
                { value: '', label: 'Portföy seçiniz...' },
                ...portfolios.map(p => ({
                  value: p.id,
                  label: `${p.title} - ${p.city?.name || ''} ${p.district?.name || ''}`
                }))
              ]}
            />
            <p className="text-sm text-blue-800 mt-2">
              💡 <strong>İpucu:</strong> Bir portföy seçerseniz, aşağıdaki tercih alanları otomatik olarak doldurulacaktır.
            </p>
          </div>

          <div className="space-y-6">
            <div className="p-4 border border-gray-200 rounded-lg bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Select label="Emlak Tipi" value={propertyPreference.property_type_id}
                  onChange={(e) => handlePreferenceChange('property_type_id', e.target.value)}
                  options={options.property_types.map(t => ({ value: t.id, label: t.name }))} />

                <Select label="Ödeme Tercihi" value={propertyPreference.payment_preference_id}
                  onChange={(e) => handlePreferenceChange('payment_preference_id', e.target.value)}
                  options={options.payment_preferences?.map(t => ({ value: t.id, label: t.name })) || []} />

                <Select label="Tasarruf Nedeni" value={propertyPreference.savings_reason_id}
                  onChange={(e) => handlePreferenceChange('savings_reason_id', e.target.value)}
                  options={options.savings_reasons?.map(t => ({ value: t.id, label: t.name })) || []} />

                <Select label="İlan Tipi" value={propertyPreference.listing_type_id}
                  onChange={(e) => handlePreferenceChange('listing_type_id', e.target.value)}
                  options={options.listing_types.map(t => ({ value: t.id, label: t.name }))} />

                {options.property_types.find(t => t.id == propertyPreference.property_type_id)?.code === 'residential' && (
                  <Select label="Konut Tipi" value={propertyPreference.residential_type_id}
                    onChange={(e) => handlePreferenceChange('residential_type_id', e.target.value)}
                    options={options.residential_types.map(t => ({ value: t.id, label: t.name }))} />
                )}

                <Select label="Oda Sayısı" value={propertyPreference.room_definition_id}
                  onChange={(e) => handlePreferenceChange('room_definition_id', e.target.value)}
                  options={options.room_definitions.map(t => ({ value: t.id, label: t.name }))} />

                <Select label="Cephe" value={propertyPreference.facade_type_id}
                  onChange={(e) => handlePreferenceChange('facade_type_id', e.target.value)}
                  options={options.facade_types.map(t => ({ value: t.id, label: t.name }))} />

                <Select label="İl" value={propertyPreference.city_id}
                  onChange={(e) => handlePreferenceChange('city_id', e.target.value)}
                  options={options.cities.map(c => ({ value: c.id, label: c.name }))} />

                <Select label="İlçe" value={propertyPreference.district_id}
                  onChange={(e) => handlePreferenceChange('district_id', e.target.value)}
                  options={districts.map(d => ({ value: d.id, label: d.name }))} />

                <Select label="Mahalle" value={propertyPreference.neighborhood_id}
                  onChange={(e) => handlePreferenceChange('neighborhood_id', e.target.value)}
                  options={neighborhoods.map(n => ({ value: n.id, label: n.name }))} />

                <Input label="Min Kat" type="number" value={propertyPreference.min_floor}
                  onChange={(e) => handlePreferenceChange('min_floor', e.target.value)} />

                <Input label="Max Kat" type="number" value={propertyPreference.max_floor}
                  onChange={(e) => handlePreferenceChange('max_floor', e.target.value)} />

                <Input label="Min m²" type="number" value={propertyPreference.min_sqm}
                  onChange={(e) => handlePreferenceChange('min_sqm', e.target.value)} />

                <Input label="Max m²" type="number" value={propertyPreference.max_sqm}
                  onChange={(e) => handlePreferenceChange('max_sqm', e.target.value)} />

                <Input label="Min Fiyat (₺)" type="number" value={propertyPreference.min_price}
                  onChange={(e) => handlePreferenceChange('min_price', e.target.value)} />

                <Input label="Max Fiyat (₺)" type="number" value={propertyPreference.max_price}
                  onChange={(e) => handlePreferenceChange('max_price', e.target.value)} />

                <Input label="Min Bina Yaşı" type="number" value={propertyPreference.min_building_age}
                  onChange={(e) => handlePreferenceChange('min_building_age', e.target.value)} />

                <Input label="Max Bina Yaşı" type="number" value={propertyPreference.max_building_age}
                  onChange={(e) => handlePreferenceChange('max_building_age', e.target.value)} />

                <div className="flex items-center h-full pt-6">
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={propertyPreference.has_elevator}
                      onChange={(e) => handlePreferenceChange('has_elevator', e.target.checked)}
                      className="w-5 h-5 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Asansör Var</span>
                  </label>
                </div>
              </div>

              <div className="mt-4">
                <Textarea
                  label="Ek Notlar"
                  value={propertyPreference.additional_notes}
                  onChange={(e) => handlePreferenceChange('additional_notes', e.target.value)}
                  rows={2}
                  placeholder="Diğer tercihler veya özel istekler..."
                />
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              💡 <strong>Not:</strong> Burada eklenen gayrimenkul tercihleri, müşterinin profil sayfasına otomatik olarak kaydedilecektir.
            </p>
          </div>
        </Card>

        {/* Submit Buttons */}
        <div className="flex gap-3 justify-end">
          <Button type="button" variant="secondary" onClick={() => navigate(`/meetings/${id}`)}>
            İptal
          </Button>
          <Button type="submit" disabled={submitting}>
            <CheckCircle size={16} />
            {submitting ? 'Kaydediliyor...' : meeting.is_completed ? 'Görüşmeyi Güncelle' : 'Görüşmeyi Tamamla'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default MeetingComplete;
